import { useCallback } from 'react';

import {
  MAX_SEQUENCE,
  useCareerFeedAIAssistant,
} from '../hooks/useCareerFeedAIAssistant';

import {
  CareerFeedAssistantQuestion,
  type CareerFeedAssistantQuestionSuggestion,
} from './CareerFeedAssistantQuestion';

export const CareerFeedAssistantQuestions = () => {
  const {
    invokeSendMessage,
    invokeSendFeedback,
    sendMessageError,
    lastCompletedAssistantResponse,
    isAssistantResponsePending,
    sequence,
    tracking: {
      trackGenAIChatOutputDisplayed,
      trackGenAIChatInputPressed: trackGenAIChatInputSubmitPressed,
      trackGenAIChatInputSucceeded: trackGenAIChatInputSubmitted,
      trackGenAIChatFeedbackPressed,
    },
  } = useCareerFeedAIAssistant();

  const onSendMessage = useCallback(
    async (
      message: string,
      skip: boolean,
      selectedSuggestions: CareerFeedAssistantQuestionSuggestion[],
    ) => {
      const submitPressedPayload = trackGenAIChatInputSubmitPressed(
        message,
        selectedSuggestions.map(({ label, showFreeTextField }) => ({
          label,
          isFreeTextInput: Boolean(showFreeTextField),
        })),
      );

      const prompt = selectedSuggestions
        .map(({ nextPrompt }) => nextPrompt)
        .concat(message)
        .filter((userPrompt) => userPrompt && userPrompt.length !== 0)
        .join('. ');

      const error = await invokeSendMessage(prompt, skip);

      if (!error) {
        if (submitPressedPayload) {
          trackGenAIChatInputSubmitted(submitPressedPayload);
        }
      } else {
        trackGenAIChatOutputDisplayed(error);
      }
    },
    [
      invokeSendMessage,
      trackGenAIChatInputSubmitPressed,
      trackGenAIChatInputSubmitted,
      trackGenAIChatOutputDisplayed,
    ],
  );

  const onSendFeedback = useCallback(
    async (sentiment: boolean) => {
      trackGenAIChatFeedbackPressed(sentiment);
      invokeSendFeedback(sentiment);
    },
    [invokeSendFeedback, trackGenAIChatFeedbackPressed],
  );

  if (
    !lastCompletedAssistantResponse ||
    lastCompletedAssistantResponse.role !== 'assistant' ||
    lastCompletedAssistantResponse.loading
  ) {
    return null;
  }

  return (
    <CareerFeedAssistantQuestion
      message={lastCompletedAssistantResponse.text}
      suggestions={lastCompletedAssistantResponse.suggestions ?? []}
      isLoading={isAssistantResponsePending}
      onSendMessage={onSendMessage}
      showFreeTextFieldOnly={
        lastCompletedAssistantResponse.collectGeneralRequirements
      }
      sendMessageError={sendMessageError}
      onSendFeedback={onSendFeedback}
      progressBar={{
        currentQuestion: sequence,
        totalQuestions: MAX_SEQUENCE,
      }}
    />
  );
};
